import React, { useState } from 'react';
import { api, athletesAPI } from '../../api';
import { useTranslation } from '../../i18n';
import { toast } from '../common/Notifier';
import { translateErrorKey } from '../../utils/errorMessage';
import { field, primaryBtn, sectionClass } from './styles';

export const BodyTab = ({ athlete, checkAuth, highlightedId }) => {
  const { t } = useTranslation();

  // --- Недельный объём ---
  const [weeklyVolumeInput, setWeeklyVolumeInput] = useState('');
  const [savingWeeklyVolume, setSavingWeeklyVolume] = useState(false);

  // --- Контрольный результат (VDOT) ---
  const [resultDistance, setResultDistance] = useState('');
  const [resultMin, setResultMin] = useState('');
  const [resultSec, setResultSec] = useState('');
  const [savingResult, setSavingResult] = useState(false);
  const [resultMessage, setResultMessage] = useState(null);

  // --- Вес ---
  const [weight, setWeight] = useState('');
  const [savingWeight, setSavingWeight] = useState(false);

  // --- HRmax ---
  const [hrMaxData, setHrMaxData] = useState({ observed_max_hr: '' });
  const [hrEstimateData, setHrEstimateData] = useState({ race_distance_m: '5000', hr_avg_last_1km: '' });
  const [savingHr, setSavingHr] = useState(false);

  const handleSaveWeeklyVolume = async (e) => {
    e.preventDefault();
    if (!weeklyVolumeInput || isNaN(parseFloat(weeklyVolumeInput))) return;

    setSavingWeeklyVolume(true);
    try {
      await athletesAPI.updateMe({ weekly_volume_km: parseFloat(weeklyVolumeInput) });
      setWeeklyVolumeInput('');
      toast.success(t('common.success'));
      await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.message || t('error.general'));
    } finally {
      setSavingWeeklyVolume(false);
    }
  };

  const handleSaveResult = async (e) => {
    e.preventDefault();
    if (!resultDistance || resultMin === '' || resultSec === '') return;

    setSavingResult(true);
    try {
      const { data } = await api.post('/race-results/', {
        athlete: athlete.id,
        distance_m: parseFloat(resultDistance),
        time_min: parseInt(resultMin, 10),
        time_sec: parseInt(resultSec, 10),
        date: new Date().toISOString().slice(0, 10),
        source: 'assessment',
      });

      setResultMessage({ vdot: data.calculated_vdot, commentKey: data.vdot_comment_key });
      toast.success(t('preferences.result_saved'));
      setResultDistance('');
      setResultMin('');
      setResultSec('');
      await checkAuth();
    } catch (error) {
      toast.error(translateErrorKey(error.response?.data?.error, t));
    } finally {
      setSavingResult(false);
    }
  };

  const handleSaveWeight = async (e) => {
    e.preventDefault();
    if (!weight || isNaN(parseFloat(weight))) return;

    setSavingWeight(true);
    try {
      await athletesAPI.addWeight(athlete.id, parseFloat(weight));
      setWeight('');
      toast.success(t('common.weight_saved'));
      await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setSavingWeight(false);
    }
  };

  const handleAddHrMax = async (e) => {
    e.preventDefault();
    if (!hrMaxData.observed_max_hr) {
      toast.error(t('profile.hr_max_required'));
      return;
    }
    setSavingHr(true);
    try {
      await athletesAPI.addHrMaxObservation(athlete.id, parseInt(hrMaxData.observed_max_hr, 10));
      await checkAuth();
      toast.success(t('profile.hr_max_added'));
      setHrMaxData({ observed_max_hr: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setSavingHr(false);
    }
  };

  const handleAddHrEstimate = async (e) => {
    e.preventDefault();
    if (!hrEstimateData.hr_avg_last_1km) {
      toast.error(t('profile.hr_estimate_required'));
      return;
    }
    setSavingHr(true);
    try {
      await athletesAPI.addHrMaxEstimate(
        athlete.id,
        parseInt(hrEstimateData.race_distance_m, 10),
        parseInt(hrEstimateData.hr_avg_last_1km, 10)
      );
      await checkAuth();
      toast.success(t('profile.hr_estimate_added'));
      setHrEstimateData({ race_distance_m: '5000', hr_avg_last_1km: '' });
    } catch (error) {
      toast.error(error.response?.data?.error || t('error.general'));
    } finally {
      setSavingHr(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Ряд 1: вес | недельный объём */}
      <form
        id="weight-section"
        onSubmit={handleSaveWeight}
        className={sectionClass('weight-section', highlightedId, 'gap-3')}
      >
        <h3 className="text-lg font-semibold text-gray-800">⚖️ {t('preferences.weight_title')}</h3>

        {athlete?.weight && (
          <p className="text-sm text-gray-500">
            {t('preferences.current_weight')}:{' '}
            <span className="font-semibold text-gray-700">{t('common.kg', { weight: athlete.weight })}</span>
          </p>
        )}

        <div className="flex gap-3 mt-auto">
          <input
            type="number"
            step="0.1"
            min="0"
            inputMode="decimal"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder={t('preferences.weight_placeholder')}
            className={`flex-1 min-w-0 ${field}`}
          />
          <button
            type="submit"
            disabled={savingWeight}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
          >
            {t('preferences.save')}
          </button>
        </div>
      </form>

      <form
        id="weekly-volume-section"
        onSubmit={handleSaveWeeklyVolume}
        className={sectionClass('weekly-volume-section', highlightedId, 'gap-3')}
      >
        <h3 className="text-lg font-semibold text-gray-800">📏 {t('preferences.weekly_volume_title')}</h3>

        {athlete?.weekly_volume_km && (
          <p className="text-sm text-gray-500">
            {t('preferences.current_weekly_volume')}:{' '}
            <span className="font-semibold text-gray-700">{athlete.weekly_volume_km} {t('common.km')}</span>
          </p>
        )}

        <div className="flex gap-3 mt-auto">
          <input
            type="number"
            step="1"
            min="0"
            inputMode="numeric"
            value={weeklyVolumeInput}
            onChange={(e) => setWeeklyVolumeInput(e.target.value)}
            placeholder={t('preferences.weekly_volume_placeholder')}
            className={`flex-1 min-w-0 ${field}`}
          />
          <button
            type="submit"
            disabled={savingWeeklyVolume}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
          >
            {t('preferences.save')}
          </button>
        </div>
      </form>

      {/* Ряд 2: VDOT | пульс */}
      <form
        id="vdot-section"
        onSubmit={handleSaveResult}
        className={sectionClass('vdot-section', highlightedId, 'gap-3')}
      >
        <h3 className="text-lg font-semibold text-gray-800">🏁 {t('preferences.result_title')}</h3>

        {athlete?.current_vdot && (
          <p className="text-sm text-gray-500">
            {t('preferences.current_vdot')}:{' '}
            <span className="font-semibold text-gray-700">{athlete.current_vdot}</span>
          </p>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('preferences.distance')}</label>
            <input type="number" inputMode="numeric" min="0" value={resultDistance}
                   onChange={(e) => setResultDistance(e.target.value)} placeholder="5000" className={field} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('preferences.minutes')}</label>
            <input type="number" inputMode="numeric" min="0" value={resultMin}
                   onChange={(e) => setResultMin(e.target.value)} placeholder="20" className={field} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('preferences.seconds')}</label>
            <input type="number" inputMode="numeric" min="0" max="59" value={resultSec}
                   onChange={(e) => setResultSec(e.target.value)} placeholder="00" className={field} />
          </div>
        </div>

        {resultMessage && (
          <div className="p-3 bg-blue-50 rounded-lg text-sm text-gray-700">
            VDOT: <strong>{resultMessage.vdot}</strong>
            {resultMessage.commentKey && <div className="mt-1">{t(resultMessage.commentKey)}</div>}
          </div>
        )}

        <button type="submit" disabled={savingResult} className={primaryBtn}>
          {savingResult ? t('common.loading') : t('preferences.save')}
        </button>
      </form>

      <div id="hr-max-section" className={sectionClass('hr-max-section', highlightedId)}>
        <h3 className="text-lg font-semibold text-gray-800">❤️ {t('profile.hr_max')}</h3>

        <div className="bg-red-50 rounded-lg p-4">
          <div className="text-sm text-gray-600">{t('profile.effective_hr_max')}</div>
          <div className="text-3xl font-bold text-red-600">{athlete?.effective_max_hr || '—'}</div>
          {athlete?.easy_hr_cap_range && (
            <div className="text-xs text-gray-500 mt-1">
              {t('profile.easy_hr_cap')}: {athlete.easy_hr_cap_range.low}–{athlete.easy_hr_cap_range.high}
            </div>
          )}
        </div>

        <form onSubmit={handleAddHrMax} className="flex items-end gap-2">
          <div className="flex-1 min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.add_hr_max')}</label>
            <input
              type="number"
              inputMode="numeric"
              min="0"
              value={hrMaxData.observed_max_hr}
              onChange={(e) => setHrMaxData({ observed_max_hr: e.target.value })}
              placeholder={t('profile.hr_max_placeholder')}
              className={field}
            />
          </div>
          <button
            type="submit"
            disabled={savingHr}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 whitespace-nowrap"
          >
            {t('profile.add_hr_max_btn')}
          </button>
        </form>

        <form onSubmit={handleAddHrEstimate} className="space-y-2 mt-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.race_distance')}</label>
              <select
                value={hrEstimateData.race_distance_m}
                onChange={(e) => setHrEstimateData({ ...hrEstimateData, race_distance_m: e.target.value })}
                className={field}
              >
                <option value="5000">5 {t('common.km')}</option>
                <option value="10000">10 {t('common.km')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.hr_avg_last_1km')}</label>
              <input
                type="number"
                inputMode="numeric"
                min="0"
                value={hrEstimateData.hr_avg_last_1km}
                onChange={(e) => setHrEstimateData({ ...hrEstimateData, hr_avg_last_1km: e.target.value })}
                placeholder={t('profile.hr_avg_placeholder')}
                className={field}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={savingHr}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {t('profile.calculate')}
          </button>
        </form>
      </div>
    </div>
  );
};